import { HTMLAttributes, ReactNode } from 'react';
import { cn } from '../../lib/cn';

/**
 * Section — bloque vertical con padding y fondo opcional.
 *
 * Tokens de padding mapeados a la escala 8 (mobile -> desktop):
 *  - sm: 32 / 48
 *  - md: 40 / 64
 *  - lg: 48 / 96 (default)
 *  - xl: 80 / 128 (hero)
 *
 * Fondos: token-only (cream-50, cream-100, primary-700, primary-900).
 */
export type SectionPadding = 'sm' | 'md' | 'lg' | 'xl' | 'none';
export type SectionBackground =
  | 'cream-50'
  | 'cream-100'
  | 'cream-200'
  | 'primary-700'
  | 'primary-900'
  | 'transparent';

export interface SectionProps extends HTMLAttributes<HTMLElement> {
  padding?: SectionPadding;
  background?: SectionBackground;
  children: ReactNode;
}

const paddingClasses: Record<SectionPadding, string> = {
  none: '',
  sm: 'py-8 lg:py-12',
  md: 'py-10 lg:py-16',
  lg: 'py-12 lg:py-24',
  xl: 'py-20 lg:py-32',
};

const backgroundClasses: Record<SectionBackground, string> = {
  'cream-50': 'bg-cream-50 text-primary-900',
  'cream-100': 'bg-cream-100 text-primary-900',
  'cream-200': 'bg-cream-200 text-primary-900',
  'primary-700': 'bg-primary-700 text-cream-100',
  'primary-900': 'bg-primary-900 text-cream-100',
  transparent: 'bg-transparent',
};

/**
 * Backgrounds que el Navbar interpreta como "oscuros" para activar el modo
 * de contraste alto (texto crema sobre transparente / sobre la sección).
 * Se emite el atributo `data-dark-bg="true"` para que el Navbar lo detecte
 * sin acoplarse a rutas.
 */
const DARK_BACKGROUNDS: ReadonlySet<SectionBackground> = new Set([
  'primary-700',
  'primary-900',
]);

export function Section({
  padding = 'lg',
  background = 'transparent',
  className,
  children,
  ...rest
}: SectionProps) {
  const isDark = DARK_BACKGROUNDS.has(background);
  return (
    <section
      data-dark-bg={isDark ? 'true' : undefined}
      className={cn(paddingClasses[padding], backgroundClasses[background], className)}
      {...rest}
    >
      {children}
    </section>
  );
}
